import { createAuthClient } from 'better-auth/react';
import { useSyncExternalStore } from 'react';
import { isPreviewMode } from '@/lib/preview/mode';
import { previewSession } from '@/lib/preview/session';

// Create Better Auth client for use in React components
const _realClient = createAuthClient({
  baseURL: process.env.NEXT_PUBLIC_BETTER_AUTH_URL || 'http://localhost:3000',
});

/**
 * Preview-mode session toggle. The flag lives in module memory so it resets
 * on hard reload — initial state is "signed in" (preview shows authenticated
 * screens), and clicking Sign Out flips to "signed out" until you reload.
 * Submitting any sign-in / sign-up form flips back. useSyncExternalStore
 * drives client re-renders; SSR returns the signed-in snapshot so hydration
 * matches the navbar's initialSession prop.
 */
let previewSignedOut = false;
const previewSubscribers = new Set<() => void>();
const subscribePreview = (cb: () => void) => {
  previewSubscribers.add(cb);
  return () => {
    previewSubscribers.delete(cb);
  };
};
const notifyPreview = () => {
  for (const cb of previewSubscribers) cb();
};
const getPreviewSnapshot = () => previewSignedOut;
const getPreviewServerSnapshot = () => false;

type FetchCallbacks = {
  onSuccess?: (ctx: unknown) => void | Promise<void>;
  onError?: (ctx: unknown) => void | Promise<void>;
};

const _previewClient = {
  useSession: () => {
    const signedOut = useSyncExternalStore(
      subscribePreview,
      getPreviewSnapshot,
      getPreviewServerSnapshot,
    );
    return {
      data: signedOut ? null : previewSession,
      isPending: false,
      error: null,
      refetch: () => {},
    };
  },
  signIn: {
    email: async (_credentials: unknown, callbacks?: FetchCallbacks) => {
      previewSignedOut = false;
      notifyPreview();
      const result = { data: previewSession, error: null };
      await callbacks?.onSuccess?.(result);
      return result;
    },
  },
  signUp: {
    email: async (_credentials: unknown, callbacks?: FetchCallbacks) => {
      previewSignedOut = false;
      notifyPreview();
      const result = { data: previewSession, error: null };
      await callbacks?.onSuccess?.(result);
      return result;
    },
  },
  signOut: async (options?: { fetchOptions?: FetchCallbacks }) => {
    previewSignedOut = true;
    notifyPreview();
    const result = { data: { success: true }, error: null };
    await options?.fetchOptions?.onSuccess?.(result);
    return result;
  },
  forgetPassword: async () => ({ data: { status: true }, error: null }),
  resetPassword: async () => ({ data: { status: true }, error: null }),
  sendVerificationEmail: async () => ({ data: { status: true }, error: null }),
} as unknown as typeof _realClient;

export const authClient = isPreviewMode() ? _previewClient : _realClient;

// Hooks exported from the client for use in components
// Usage in components:
// const session = authClient.useSession();
// const { data: session, isPending, error } = session;

// Sign in example:
// await authClient.signIn.email({
//   email: 'user@example.com',
//   password: 'password',
//   callbackURL: '/dashboard'
// });

// Sign up example:
// await authClient.signUp.email({
//   email: 'user@example.com',
//   password: 'password',
//   name: 'John Doe',
//   callbackURL: '/dashboard'
// });

// Sign out example:
// await authClient.signOut();
